import React from "react"
import { useLocation } from "@reach/router"
import { Link } from "gatsby"
import { Box, Label } from "~common/components/Primitives"
import Typography from "~common/components/Typography"
import Button from "~common/components/Button"
import {
  analyticsConfig,
  applyConsentPreferences,
  getConsentPreferences,
  trackPageView,
} from "~analytics"
import { hasStoredConsentChoice } from "~analytics/consent"

const PreferenceRow = ({
  title,
  description,
  checked,
  disabled = false,
  onChange,
}) => (
  <Label
    sx={{
      display: "grid",
      gridTemplateColumns: "auto 1fr",
      columnGap: 3,
      alignItems: "start",
      py: 3,
      color: "primary",
    }}
  >
    <Box
      as="input"
      type="checkbox"
      checked={checked}
      disabled={disabled}
      readOnly={disabled}
      onChange={onChange}
      sx={{
        width: 18,
        height: 18,
        mt: "0.15rem",
      }}
    />
    <Box>
      <Typography variant="small" as="span" sx={{ color: "primary" }}>
        {title}
      </Typography>
      <Typography
        variant="tiny"
        as="p"
        sx={{ color: "primary", opacity: 0.82, mt: 1, lineHeight: 1.5 }}
      >
        {description}
      </Typography>
    </Box>
  </Label>
)

const ConsentBanner = () => {
  const location = useLocation()
  const [isReady, setIsReady] = React.useState(false)
  const [isOpen, setIsOpen] = React.useState(false)
  const [showPreferences, setShowPreferences] = React.useState(false)
  const [marketing, setMarketing] = React.useState(false)

  React.useEffect(() => {
    if (!analyticsConfig.marketing.showConsentBanner) {
      setIsReady(true)
      return
    }

    const preferences = getConsentPreferences()
    setMarketing(preferences.marketing)
    setIsOpen(!hasStoredConsentChoice())
    setIsReady(true)
  }, [])

  if (!analyticsConfig.marketing.showConsentBanner || !isReady) {
    return null
  }

  const commitPreferences = preferences => {
    const consent = applyConsentPreferences(preferences)
    setMarketing(consent.preferences.marketing)
    setIsOpen(false)
    setShowPreferences(false)

    if (consent.preferences.marketing) {
      trackPageView(location)
    }
  }

  const handleRejectAll = () => {
    commitPreferences({ marketing: false })
  }

  const handleAcceptAll = () => {
    commitPreferences({ marketing: true })
  }

  const handleSavePreferences = () => {
    commitPreferences({ marketing })
  }

  return (
    <>
      {isOpen && (
        <Box
          role="dialog"
          aria-label="Privacy preferences"
          sx={{
            zIndex: 1000,
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            px: [4, 5],
            py: 4,
            bg: "secondary",
            borderTop: theme =>
              `1px solid ${theme.colors["softened-secondary"]}`,
            boxShadow: "0 -8px 24px rgba(0, 0, 0, 0.18)",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: ["1fr", "1fr auto"],
              gap: [3, 5],
              alignItems: "center",
              maxWidth: 1180,
              mx: "auto",
            }}
          >
            <Box>
              <Typography
                variant="normal"
                as="h2"
                sx={{ color: "primary", mb: 2, lineHeight: 1.2 }}
              >
                Privacy preferences
              </Typography>
              <Typography
                variant="small"
                as="p"
                sx={{ color: "primary", lineHeight: 1.6 }}
              >
                Almagesto uses necessary local preferences and may use
                privacy-first analytics. Marketing tags are loaded only in
                campaign mode after your consent.{" "}
                <Box
                  as="span"
                  sx={{
                    a: {
                      color: "softened-accent",
                      "&:hover": { color: "accent" },
                    },
                  }}
                >
                  <Link to="/privacy">Privacy Policy</Link>
                </Box>{" "}
                and{" "}
                <Box
                  as="span"
                  sx={{
                    a: {
                      color: "softened-accent",
                      "&:hover": { color: "accent" },
                    },
                  }}
                >
                  <Link to="/cookies">Cookie Policy</Link>
                </Box>
                .
              </Typography>
              {showPreferences && (
                <Box sx={{ mt: 3 }}>
                  <PreferenceRow
                    title="Necessary"
                    description="Required for local preferences, checkout redirects and site security."
                    checked
                    disabled
                  />
                  <PreferenceRow
                    title="Analytics privacy-first"
                    description="Cookieless aggregate analytics such as Plausible, when enabled."
                    checked
                    disabled
                  />
                  <PreferenceRow
                    title="Marketing / advertising"
                    description="Google, social ads or conversion tags for campaign measurement."
                    checked={marketing}
                    onChange={event => setMarketing(event.target.checked)}
                  />
                </Box>
              )}
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: ["1fr 1fr", "repeat(3, auto)"],
                gap: 3,
                justifyContent: ["stretch", "end"],
                alignItems: "center",
              }}
            >
              <Button
                type="button"
                variant="primary"
                onClick={handleRejectAll}
                sx={{ minWidth: 128 }}
              >
                Reject all
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleAcceptAll}
                sx={{ minWidth: 128 }}
              >
                Accept all
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={
                  showPreferences
                    ? handleSavePreferences
                    : () => setShowPreferences(true)
                }
                sx={{ gridColumn: ["1 / -1", "auto"], minWidth: 128 }}
              >
                {showPreferences ? "Save preferences" : "Preferences"}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
      {!isOpen && (
        <Box
          as="button"
          type="button"
          onClick={() => setIsOpen(true)}
          sx={{
            zIndex: 999,
            position: "fixed",
            left: [3, 4],
            bottom: [3, 4],
            px: 3,
            py: 2,
            bg: "secondary",
            color: "primary",
            border: theme => `1px solid ${theme.colors["softened-secondary"]}`,
            borderRadius: 4,
            cursor: "pointer",
            fontSize: 0,
          }}
        >
          Privacy settings
        </Box>
      )}
    </>
  )
}

export default ConsentBanner
